import { useEffect, useRef } from 'react';
import type { MapAdapter, MapPoint } from './types';

export interface ArcGisMapCanvasProps {
  points: MapPoint[];
  selectedId?: string;
  onSelect?: (id: string) => void;
  center: { lat: number; lng: number };
  zoom: number;
  minZoom: number;
  basemap: string;
  className?: string;
  onReady: (adapter: MapAdapter) => void;
  onDestroyed: () => void;
  onError: () => void;
}

/**
 * Hosts the ArcGIS MapView. The SDK is loaded with a dynamic import so it is not
 * in the main bundle; Index list/split views that never open the map never download it.
 */
export const ArcGisMapCanvas = ({
  points,
  selectedId,
  onSelect,
  center,
  zoom,
  minZoom,
  basemap,
  className,
  onReady,
  onDestroyed,
  onError,
}: ArcGisMapCanvasProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const adapterRef = useRef<MapAdapter | null>(null);
  const pointsRef = useRef(points);
  const selectedIdRef = useRef(selectedId);
  const onSelectRef = useRef(onSelect);
  const onReadyRef = useRef(onReady);
  const onDestroyedRef = useRef(onDestroyed);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    pointsRef.current = points;
    selectedIdRef.current = selectedId;
    onSelectRef.current = onSelect;
    onReadyRef.current = onReady;
    onDestroyedRef.current = onDestroyed;
    onErrorRef.current = onError;
  });

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;
    let adapter: MapAdapter | undefined;

    void import('./createArcGisAdapter')
      .then(({ createArcGisAdapter }) => {
        if (cancelled) return undefined;
        return createArcGisAdapter({
          container,
          center: [center.lng, center.lat],
          zoom,
          minZoom,
          basemap,
          onSelect: (id) => onSelectRef.current?.(id),
        });
      })
      .then((created) => {
        if (!created) return;
        if (cancelled) {
          created.destroy();
          return;
        }
        adapter = created;
        adapterRef.current = created;
        created.setPoints(pointsRef.current);
        if (selectedIdRef.current) created.focusPoint(selectedIdRef.current);
        onReadyRef.current(created);
      })
      .catch((error: unknown) => {
        console.error('Failed to load the ArcGIS map', error);
        if (!cancelled) onErrorRef.current();
      });

    return () => {
      cancelled = true;
      adapterRef.current = null;
      adapter?.destroy();
      onDestroyedRef.current();
    };
    // The view is created once per mount. Retry remounts this component via `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- map options are init-only
  }, []);

  useEffect(() => {
    adapterRef.current?.setPoints(points);
  }, [points]);

  useEffect(() => {
    adapterRef.current?.focusPoint(selectedId ?? '');
  }, [selectedId]);

  return <div ref={containerRef} className={className} />;
};
